import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { BasketLearningQuestionResult, FinishPlayAttemptInput } from '../../../shared/results';
import { FullscreenGameShell } from '../../game/FullscreenGameShell';
import { BasketAudio } from './BasketAudio';
import type { BasketLearningConfig } from './domain/basketLevels';
import { applyBasketAnswer, initialBasketRuleState, type BasketRuleState } from './domain/basketRules';
import {
  autoMissPlan, basketAimRules, buildBasketShotPlan, classifyBasketAim, initialBasketKeyboardAim, intersectHoopPlane, moveBasketKeyboardAim,
  type BasketAimPoint, type BasketAimZone, type BasketShotOutcome,
} from './domain/basketAimModel';
import { basketSceneLayout } from './three/basketSceneLayout';

type ArenaController = import('./three/BasketArenaController').BasketArenaController;
type Props = {
  config: BasketLearningConfig;
  label?: string;
  restartEnabled?: boolean;
  onFinished?: (result: FinishPlayAttemptInput) => void;
};
/** question: kart bekleniyor · aim: doğru cevap, öğrenci nişan alıyor · own-shot: öğrencinin atışı havada · reveal: yanlış, doğru cevap gösteriliyor · auto-shot: yanlışın otomatik atışı */
type Phase = 'question' | 'aim' | 'own-shot' | 'reveal' | 'auto-shot' | 'finished';
type ScreenPoint = { x: number; y: number };
type Drag = { end: ScreenPoint; zone: BasketAimZone };
type PendingAnswer = {
  choiceIndex: number; correctChoiceIndex: number; durationMs: number;
  calculation: ReturnType<typeof applyBasketAnswer>; selectedAnswer: string; correctAnswer: string;
};

/** Yanlışta doğru cevabın otomatik atıştan önce görünme süresi ve atış sonrası okuma süreleri (ms). */
const REVEAL_MS = 1500;
const AFTER_BASKET_MS = 900;
const AFTER_AUTO_SHOT_MS = 1900;

function answerLabel(text: string, hasMedia: boolean) {
  return text.trim() || (hasMedia ? 'Görsel seçenek' : 'Boş seçenek');
}
function missLabel(outcome: BasketShotOutcome) {
  if (outcome === 'backboard') return 'Panyadan döndü!';
  if (outcome === 'airball') return 'Top potaya hiç değmedi!';
  return 'Çemberden döndü!';
}
const zoneTone: Record<BasketAimZone, 'inside' | 'outside' | 'short'> = { basket: 'inside', rim: 'outside', backboard: 'outside', airball: 'outside', short: 'short' };

export function BasketLearningGame({ config, label = 'Potada Öğren 3B oyun alanı', restartEnabled = true, onFinished }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const gameRef = useRef<ArenaController | null>(null);
  const audioRef = useRef(new BasketAudio());
  const onFinishedRef = useRef(onFinished);
  const questionStartedAt = useRef(performance.now());
  const runStartedAt = useRef(performance.now());
  const resultSent = useRef(false);
  const pending = useRef<PendingAnswer | null>(null);
  const attemptsRef = useRef(0);
  const rulesRef = useRef<BasketRuleState>(initialBasketRuleState());
  const resultsRef = useRef<BasketLearningQuestionResult[]>([]);
  const questionIndexRef = useRef(0);
  const disposed = useRef(false);
  const timers = useRef(new Set<number>());
  const [questionIndex, setQuestionIndex] = useState(0);
  const [rules, setRules] = useState<BasketRuleState>(initialBasketRuleState());
  const [questionResults, setQuestionResults] = useState<BasketLearningQuestionResult[]>([]);
  const [feedback, setFeedback] = useState<string | null>('Final karşılaşması başlıyor!');
  const [feedbackTone, setFeedbackTone] = useState<'neutral' | 'shot' | 'correct' | 'wrong'>('neutral');
  const [muted, setMuted] = useState(() => audioRef.current.isMuted());
  const [phase, setPhase] = useState<Phase>('question');
  const [ready, setReady] = useState(false);
  const [answer, setAnswer] = useState<{ selected: number; correct: number } | null>(null);
  const [misses, setMisses] = useState(0);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [keyboardAim, setKeyboardAim] = useState<BasketAimPoint | null>(null);
  const [geometry, setGeometry] = useState<{ ball: ScreenPoint & { radius: number }; frame: ScreenPoint[] } | null>(null);

  const wait = (ms: number) => new Promise<void>((resolve) => {
    const id = window.setTimeout(() => { timers.current.delete(id); resolve(); }, ms); timers.current.add(id);
  });

  useEffect(() => { onFinishedRef.current = onFinished; }, [onFinished]);

  useLayoutEffect(() => {
    disposed.current = false;
    let cancelled = false;
    void import('./three/BasketArenaController').then(({ BasketArenaController }) => {
      if (cancelled || !containerRef.current) return;
      gameRef.current = new BasketArenaController(containerRef.current);
      setReady(true);
    });
    const pendingTimers = timers.current;
    return () => {
      cancelled = true; disposed.current = true; pendingTimers.forEach((id) => window.clearTimeout(id)); pendingTimers.clear();
      gameRef.current?.dispose(); gameRef.current = null;
      audioRef.current.dispose();
    };
  }, []);

  const measure = useCallback(() => {
    const game = gameRef.current; if (!game) return;
    setGeometry({ ball: game.ballScreen(), frame: game.targetFrameScreen() });
  }, []);
  useEffect(() => {
    if (phase !== 'aim' || !ready) return;
    measure();
    const observer = new ResizeObserver(measure); if (overlayRef.current) observer.observe(overlayRef.current);
    return () => observer.disconnect();
  }, [measure, phase, ready]);

  const current = config.questions[questionIndex];

  const finish = (nextRules: BasketRuleState, nextResults: BasketLearningQuestionResult[]) => {
    setPhase('finished'); setFeedback(nextRules.correct === config.questions.length ? 'Kusursuz final!' : 'Karşılaşma tamamlandı!');
    setFeedbackTone('correct');
    audioRef.current.finish(nextRules.correct === config.questions.length);
    if (resultSent.current) return;
    resultSent.current = true;
    onFinishedRef.current?.({
      score: nextRules.score, lives: 0, correct: nextRules.correct, wrong: nextRules.wrong,
      attempts: nextRules.answered, completed: true,
      details: { templateKey: 'basket-learning', maxStreak: nextRules.maxStreak, questions: nextResults },
    });
  };

  /** Sorunun kaydını yazar ve sonraki soruya/sonuca geçer. Puan kuralı cevap anında hesaplanan değerdir. */
  const commit = (shotAttempts: number) => {
    const answered = pending.current; const question = config.questions[questionIndexRef.current];
    if (!answered || !question) return;
    const record: BasketLearningQuestionResult = {
      level: question.id, durationMs: answered.durationMs, selectedChoiceIndex: answered.choiceIndex, correctChoiceIndex: answered.correctChoiceIndex,
      correct: answered.choiceIndex === answered.correctChoiceIndex, shotAttempts: Math.min(basketAimRules.maxRecordedAttempts, shotAttempts),
      baseScore: answered.calculation.breakdown.baseScore, streakBonus: answered.calculation.breakdown.streakBonus,
      scoreDelta: answered.calculation.scoreDelta, questionPrompt: question.prompt,
      selectedAnswer: answered.selectedAnswer, correctAnswer: answered.correctAnswer,
    };
    const nextResults = [...resultsRef.current, record]; resultsRef.current = nextResults; setQuestionResults(nextResults);
    rulesRef.current = answered.calculation.state; setRules(answered.calculation.state);
    pending.current = null; setAnswer(null); setMisses(0); setDrag(null); setKeyboardAim(null); attemptsRef.current = 0;
    if (questionIndexRef.current + 1 >= config.questions.length) { finish(answered.calculation.state, nextResults); return; }
    questionIndexRef.current += 1; setQuestionIndex(questionIndexRef.current);
    questionStartedAt.current = performance.now(); setFeedback(null); setFeedbackTone('neutral'); setPhase('question');
  };

  const choose = async (choiceIndex: number) => {
    if (!ready || phase !== 'question' || !current) return;
    const choice = current.choices[choiceIndex];
    const correctChoiceIndex = current.choices.findIndex((candidate) => candidate.correct);
    const correct = Boolean(choice?.correct);
    pending.current = {
      choiceIndex, correctChoiceIndex, durationMs: Math.max(0, Math.round(performance.now() - questionStartedAt.current)),
      calculation: applyBasketAnswer(rulesRef.current, correct),
      selectedAnswer: answerLabel(choice?.label ?? '', Boolean(choice?.mediaUrl)),
      correctAnswer: answerLabel(current.choices[correctChoiceIndex]?.label ?? '', Boolean(current.choices[correctChoiceIndex]?.mediaUrl)),
    };
    setAnswer({ selected: choiceIndex, correct: correctChoiceIndex });
    if (correct) {
      attemptsRef.current = 0; setMisses(0); setKeyboardAim(null);
      setPhase('aim'); setFeedbackTone('correct');
      setFeedback('Doğru cevap! Şimdi topu tutup potaya doğru sürükle ve atışını kendin yap.');
      return;
    }
    setPhase('reveal'); setFeedbackTone('wrong'); setFeedback(`Yanlış cevap. Doğru cevap: ${pending.current.correctAnswer}`);
    await wait(REVEAL_MS); if (disposed.current) return;
    setPhase('auto-shot'); setFeedbackTone('shot'); setFeedback('Atış otomatik yapılıyor…'); audioRef.current.shotRelease();
    await gameRef.current?.shoot(autoMissPlan(Math.random()), (outcome) => {
      if (outcome !== 'airball') audioRef.current.impact(outcome, 0);
      setFeedbackTone('wrong'); setFeedback(`${missLabel(outcome)} Doğru cevabı verseydin atışı kendin yapacaktın!`);
    });
    if (disposed.current) return;
    await wait(AFTER_AUTO_SHOT_MS); if (disposed.current) return;
    commit(1);
  };

  const shootAt = async (point: BasketAimPoint | null, zone: BasketAimZone) => {
    if (phase !== 'aim' || !pending.current) return;
    setDrag(null);
    if (!point || zone === 'short') { setFeedbackTone('neutral'); setFeedback('Topu potaya doğru sürükle; çizgi potanın üstünde yeşil olur.'); return; }
    attemptsRef.current += 1; const attempts = attemptsRef.current;
    const plan = buildBasketShotPlan(point, zone);
    setPhase('own-shot'); setFeedbackTone('shot'); setFeedback('ATIŞ HAVADA…'); audioRef.current.shotRelease();
    const streak = pending.current.calculation.state.streak; const { scoreDelta, breakdown } = pending.current.calculation;
    await gameRef.current?.shoot(plan, (outcome) => {
      if (outcome !== 'airball') audioRef.current.impact(outcome, streak);
      if (outcome === 'swish') {
        setFeedbackTone('correct');
        setFeedback(`BASKET! +${scoreDelta}${breakdown.streakBonus ? ` · Seri bonusu +${breakdown.streakBonus}` : ''}${attempts > 1 ? ` · ${attempts}. atışta` : ''}`);
      } else {
        setFeedbackTone('wrong'); setFeedback(`${missLabel(outcome)} Potayı hedefle ve tekrar dene.`);
      }
    });
    if (disposed.current) return;
    if (plan.outcome === 'swish') { await wait(AFTER_BASKET_MS); if (!disposed.current) commit(attempts); return; }
    setMisses((count) => count + 1); setPhase('aim');
  };

  const localPoint = (event: ReactPointerEvent<HTMLDivElement>): ScreenPoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };
  const aimAt = (end: ScreenPoint): { point: BasketAimPoint | null; zone: BasketAimZone } => {
    const game = gameRef.current; const ball = geometry?.ball;
    if (!game || !ball || Math.hypot(end.x - ball.x, end.y - ball.y) < basketAimRules.minDragPx) return { point: null, zone: 'short' };
    const ray = game.screenRay(end.x, end.y); const point = intersectHoopPlane(ray.origin, ray.direction);
    return { point, zone: classifyBasketAim(point) };
  };
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (phase !== 'aim' || !geometry) return;
    const point = localPoint(event);
    if (Math.hypot(point.x - geometry.ball.x, point.y - geometry.ball.y) > Math.max(basketAimRules.grabRadiusPx, geometry.ball.radius * 1.6)) {
      setFeedbackTone('neutral'); setFeedback('Topu tut ve potaya doğru sürükle.'); return;
    }
    event.currentTarget.setPointerCapture(event.pointerId); setKeyboardAim(null);
    setDrag({ end: point, zone: 'short' });
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return; const end = localPoint(event); setDrag({ end, zone: aimAt(end).zone });
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return; const { point, zone } = aimAt(localPoint(event)); void shootAt(point, zone);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const name = event.key.toLowerCase();
      if (name === 'm') { setMuted(audioRef.current.toggleMuted()); return; }
      if (phase !== 'aim') return;
      const moves: Record<string, [number, number]> = { arrowleft: [-1, 0], arrowright: [1, 0], arrowup: [0, 1], arrowdown: [0, -1] };
      if (moves[name]) { event.preventDefault(); setKeyboardAim((aim) => moveBasketKeyboardAim(aim ?? initialBasketKeyboardAim, ...moves[name])); return; }
      if (name === 'enter' || name === ' ') {
        event.preventDefault(); const aim = keyboardAim ?? initialBasketKeyboardAim; void shootAt(aim, classifyBasketAim(aim));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const keyboardEnd = keyboardAim && gameRef.current ? gameRef.current.projectToScreen({ x: keyboardAim.x, y: keyboardAim.y, z: basketSceneLayout.hoop.z }) : null;
  const aimLine = drag ? { end: drag.end, zone: drag.zone } : keyboardEnd && keyboardAim ? { end: keyboardEnd, zone: classifyBasketAim(keyboardAim) } : null;

  useEffect(() => {
    window.render_game_to_text = () => JSON.stringify({
      template: 'basket-learning', phase: ready ? phase : 'loading',
      question: Math.min(questionIndex + 1, config.questions.length), totalQuestions: config.questions.length,
      score: rules.score, streak: rules.streak, correct: rules.correct, wrong: rules.wrong,
      shotAttempts: attemptsRef.current, missedShots: misses, aimZone: aimLine?.zone ?? null,
      ball: gameRef.current?.getDebugState().ball ?? 'ready',
    });
    window.advanceTime = (ms: number) => { gameRef.current?.advanceTime(ms); };
    return () => { window.render_game_to_text = () => '{}'; window.advanceTime = () => undefined; };
  }, [aimLine?.zone, config.questions.length, misses, phase, questionIndex, ready, rules]);

  const restart = () => {
    questionIndexRef.current = 0; rulesRef.current = initialBasketRuleState(); resultsRef.current = []; pending.current = null; attemptsRef.current = 0;
    setQuestionIndex(0); setRules(initialBasketRuleState()); setQuestionResults([]);
    setFeedback('Yeni final başlıyor!'); setFeedbackTone('neutral'); setPhase('question'); setAnswer(null); setMisses(0); setDrag(null); setKeyboardAim(null);
    resultSent.current = false;
    questionStartedAt.current = performance.now(); runStartedAt.current = performance.now();
  };

  const finished = phase === 'finished';
  const shooting = phase === 'aim' || phase === 'own-shot';
  const mistakes = questionResults.filter((result) => !result.correct);
  return (
    <FullscreenGameShell gameRef={gameRef} className="basket-learning-shell" toolbar={<>
      <strong className="toolbar-level">SORU {Math.min(questionIndex + 1, config.questions.length)} / {config.questions.length}</strong>
      <span className="basket-streak">Seri: {rules.streak}</span>
      <span className="toolbar-score">Puan: {rules.score}</span>
      <button
        className="basket-sound-button"
        type="button"
        onClick={() => setMuted(audioRef.current.toggleMuted())}
        aria-pressed={muted}
        aria-label={muted ? 'Sesi aç' : 'Sesi kapat'}
        title="Ses (M)"
      >
        <span aria-hidden="true">{muted ? '🔇' : '🔊'}</span><b>{muted ? 'Ses kapalı' : 'Ses açık'}</b>
      </button>
    </>}>
      <div className="basket-stage" role="application" aria-label={label}>
        <div className="basket-three-canvas" ref={containerRef} />
        <div className="basket-arena-vignette" aria-hidden="true" />
        {!ready && <div className="basket-loading" role="status">3B arena hazırlanıyor…</div>}
        {!finished && current && (
          <div className={`basket-question-layer${shooting ? ' aiming' : ''}`}>
            <section className="basket-led-question" aria-label="Soru">
              <small>LED SKORBORD · {current.id}. ATIŞ</small>
              {current.promptMediaUrl && <img src={current.promptMediaUrl} alt="Soru görseli" />}
              {current.prompt && <h2>{current.prompt}</h2>}
            </section>
            {feedback && <p className={`basket-feedback ${feedbackTone}`} role="status">{feedback}</p>}
            <div className={`basket-choice-grid choices-${current.choices.length}`} aria-hidden={shooting}>
              {current.choices.map((choice, index) => (
                <button type="button" key={choice.id} disabled={!ready || phase !== 'question'} onClick={() => void choose(index)}
                  className={answer && index === answer.correct ? 'is-correct' : answer && index === answer.selected ? 'is-wrong' : undefined}>
                  <span className="basket-choice-number">{answer && index === answer.correct ? '✓' : answer && index === answer.selected ? '✕' : index + 1}</span>
                  {choice.mediaUrl && <img src={choice.mediaUrl} alt="" />}
                  {choice.label && <strong>{choice.label}</strong>}
                </button>
              ))}
            </div>
          </div>
        )}
        {!finished && <div ref={overlayRef} className={`basket-aim-layer${phase === 'aim' ? ' active' : ''}${drag ? ' dragging' : ''}`}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={() => setDrag(null)}
          aria-hidden={phase !== 'aim'}>
          {phase === 'aim' && geometry && <svg className="basket-aim-svg" aria-hidden="true">
            {misses >= basketAimRules.hintAfterMisses && <polygon className="basket-aim-frame" points={geometry.frame.map((point) => `${point.x},${point.y}`).join(' ')} />}
            {!aimLine && <>
              <circle className="basket-aim-ball-ring" cx={geometry.ball.x} cy={geometry.ball.y} r={geometry.ball.radius * 1.25} />
              <text className="basket-aim-hand" x={geometry.ball.x + geometry.ball.radius * 0.9} y={geometry.ball.y + geometry.ball.radius * 0.9}>👆</text>
            </>}
            {aimLine && <g className={`basket-aim-line ${zoneTone[aimLine.zone]}`}>
              <line x1={geometry.ball.x} y1={geometry.ball.y} x2={aimLine.end.x} y2={aimLine.end.y} />
              <circle cx={aimLine.end.x} cy={aimLine.end.y} r={11} />
            </g>}
          </svg>}
          {phase === 'aim' && <p className="basket-aim-help">Sürükle ve bırak · Klavye: ok tuşları ile nişan, Enter ile atış</p>}
        </div>}
        {finished && (
          <section className="basket-result-overlay" aria-label="Oyun sonucu">
            <p className="eyebrow">Maç sonu</p>
            <h2>{rules.correct === config.questions.length ? 'KUSURSUZ FİNAL!' : 'POTADA ÖĞREN TAMAMLANDI!'}</h2>
            <p>{rules.score} puan · {rules.correct}/{config.questions.length} doğru · En uzun seri {rules.maxStreak}</p>
            <p>Toplam süre {Math.max(1, Math.round((performance.now() - runStartedAt.current) / 1000))} saniye</p>
            {mistakes.length > 0 && <div className="basket-mistake-review"><strong>Tekrar göz atalım</strong><ul>
              {mistakes.map((result) => <li key={result.level}>S{result.level}: {result.questionPrompt || 'Görsel soru'} — Doğru: {result.correctAnswer}</li>)}
            </ul></div>}
            {restartEnabled && <button className="primary-button" type="button" onClick={restart}>Yeniden oyna</button>}
          </section>
        )}
      </div>
    </FullscreenGameShell>
  );
}
